import { OptionallyVisible } from 'components';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import merchantsApi from 'services/merchants';
import { CryptoPayoutSettingsPayload } from 'services/merchants/types';
import * as Yup from 'yup';

import { Button } from 'components/buttons';
import { ButtonWithLoading } from 'components/buttons/buttonWithLoading';
import { Input } from 'components/inputs';
import { Buttons } from 'components/modal/styles';

import { ReduxToolkitError } from 'types/http';

import { usePayout } from '../context';
import { usePayoutSetupStep } from '../context/modal';
import { ErrorText } from '../fiat/styles';
import { Delimiter, Label, Section } from '../styles';
import { PayoutType } from '../types';
import LABELS, { KEYS } from './keys';

const WalletDetails = () => {
  const { t } = useTranslation();
  const payoutStep = usePayoutSetupStep();
  const payout = usePayout();
  const selectedAsset = payout[PayoutType.Crypto]?.cryptocurrency;
  const [payoutSettingsMutation, { isLoading, error, reset }] = merchantsApi.useUpdateMerchantPayoutSettingsMutation();
  const backButtonLabel = payoutStep.canGoBack() ? t(LABELS.ACTIONS.BACK) : t(LABELS.ACTIONS.CANCEL);

  const onSubmit = async () => {
    const errors = await form.validateForm();
    form.setErrors(errors);
    const hasErrors = Object.keys(form.errors).length > 0;
    if (hasErrors) {
      return;
    }

    if (!selectedAsset) {
      throw new Error('Missing asset to setup crypto payout with.');
    }

    let payload: CryptoPayoutSettingsPayload = {
      assetUuid: selectedAsset.uuid,
      merchantId: payout.merchant.uuid,
      wallet: {
        address: form.values[KEYS.FORM_FIELDS.WALLET_ADDRESS],
      },
    };

    if (selectedAsset?.addressTagRegexp) {
      payload = {
        ...payload,
        wallet: {
          ...payload.wallet,
          memo: form.values[KEYS.FORM_FIELDS.WALLET_TAG],
        },
      };
    }

    const response = await payoutSettingsMutation(payload);
    if ('error' in response) {
      return;
    }

    payoutStep.proceed();
  };

  const formConfiguration = {
    initialValues: {
      [KEYS.FORM_FIELDS.WALLET_ADDRESS]: '',
      [KEYS.FORM_FIELDS.WALLET_TAG]: '',
    },
    validationSchema: Yup.object().shape({
      [KEYS.FORM_FIELDS.WALLET_ADDRESS]: Yup.string()
        .required(t(LABELS.WALLET.ADDRESS_REQUIRED_ERROR))
        .matches(new RegExp(selectedAsset?.addressRegexp ?? ''), t(LABELS.WALLET.INVALID_ADDRESS)),
      [KEYS.FORM_FIELDS.WALLET_TAG]: Yup.string().matches(new RegExp(selectedAsset?.addressTagRegexp ?? ''), t(LABELS.WALLET.INVALID_TAG)),
    }),
    onSubmit,
    validateOnChange: true,
  };
  const form = useFormik(formConfiguration);

  const onChange = (field: string) => (value: string) => {
    reset();
    form.setFieldValue(field, value);
  };

  const hasErrors = Boolean(Object.values(form.errors).length) || Boolean(error);

  return (
    <>
      <Section>
        <Label>{t(LABELS.WALLET.ADDRESS_LABEL)}</Label>
        <Input
          fullWidth
          value={form.values[KEYS.FORM_FIELDS.WALLET_ADDRESS]}
          onChange={onChange(KEYS.FORM_FIELDS.WALLET_ADDRESS)}
          placeholder={t(LABELS.WALLET.ADDRESS_PLACEHOLDER)}
        />
        <ErrorText>{form.errors[KEYS.FORM_FIELDS.WALLET_ADDRESS]}</ErrorText>
      </Section>
      <OptionallyVisible visible={Boolean(selectedAsset?.addressTagRegexp)}>
        <Section>
          <Label>{t(LABELS.WALLET.TAG_LABEL)}</Label>
          <Input
            fullWidth
            value={form.values[KEYS.FORM_FIELDS.WALLET_TAG]}
            onChange={onChange(KEYS.FORM_FIELDS.WALLET_TAG)}
            placeholder={t(LABELS.WALLET.TAG_PLACHOLDER)}
          />
          <ErrorText>{form.errors[KEYS.FORM_FIELDS.WALLET_TAG]}</ErrorText>
        </Section>
      </OptionallyVisible>
      <OptionallyVisible visible={Boolean(error)}>
        <ErrorText>{(error as ReduxToolkitError)?.data?.errorMessage}</ErrorText>
      </OptionallyVisible>
      <Delimiter />
      <Buttons>
        <Button secondary onClick={payoutStep.goBack}>
          {backButtonLabel}
        </Button>
        <ButtonWithLoading isLoading={isLoading} primary onClick={form.submitForm} disabled={hasErrors}>
          {t(LABELS.ACTIONS.ADD)}
        </ButtonWithLoading>
      </Buttons>
    </>
  );
};

export default WalletDetails;
